<template>
  <div>
    <i v-show="loading" class="icon loading" />
    <div id="container"></div>
  </div>
</template>

<script>
import Konva from 'konva';
import FamilyPreviewService from '@/services/familyPreviewServices/familyPreviewService';
import { FAMILY_PREVIEW_EDITOR_CONFIG as CONFIG } from '@/constants/familyPreviewEditor';
import { NAMES } from '@/constants/productNames';
import axios from 'axios';
import { getStaticUrl } from '@/helpers';
import StaticUrlService from '@/services/staticUrlService';

const MARGIN_RIGHT = 20;
const NAME_BLOCK_HEIGHT = 50;
const HEADER_BLOCK_HEIGHT = 48;
const CUSTOM_SELECTION_TITLE_HEIGHT = 18;
const MARGIN_HEADER_BLOCK = 24;
const CUSTOM_FONT_FAMILY = "Ericsson Hilda";

export default {
  name: 'FamilyViewerComponent',

  props: {
    customProducts: {
      type: Array,
      default() {
        return [];
      },
    },

    selectedFamily: {
      type: String,
      required: true,
    },
  },

  watch: {
    totalProducts: function () {
      if (
        this.totalProducts === this.customProducts.length ||
        this.totalProducts === this.products.length
      ) {
        this.loading = false;
        let totalWidth = 0;
        this.layer.children.forEach((c) => (totalWidth += c.width() + 40));
        
        if (totalWidth > this.layer.width() && !!this.customProducts.length) {
          this.layer.scaleX(this.layer.width() / totalWidth * 0.9);
          this.layer.scaleY(this.currentHeight * 0.9);
          this.layer.x((this.stage.width() - this.layer.getClientRect().width) / 2);
        } else {
          if (!!this.customProducts.length) {
            this.layer.scaleX(this.currentWidth * 0.9);
            this.layer.scaleY(this.currentHeight * 0.9);
            this.layer.x((this.stage.width() - this.layer.getClientRect().width) * 0.5 - this.layer.getClientRect().x);
          } else {
            this.layer.scaleY(0.9);
            this.layer.scaleX(0.9);
            this.layer.x((this.stage.width() - this.layer.getClientRect().width) * 0.5 - this.layer.getClientRect().x);
          }
        }

        this.layer.show();
      }
    },
  },


  data() {
    return {
      stage: Konva.Stage,
      layer: Konva.Layer,
      clusters: [],
      products: [],
      canvasData: {},
      baseHeight: 0,
      baseWidth: 0,
      currentHeight: 0,
      currentWidth: 0,
      currentSize: 0,
      totalProducts: 0,
      loading: true,
      buildNumber: '',
    };
  },

  created() {
    window.addEventListener('resize', () => this.changeSize());
  },

  async mounted() {
    const fullStaticUrl = await StaticUrlService.getFullStaticUrl(null, this.$route.name);

    axios
      .get(`${fullStaticUrl}productData.json?` + Date.now())
      .then((result) => {
        this.$store.commit(
          'radios',
          result.data.filter((x) => x.isMana == false)
        );

        axios
          .get(`${fullStaticUrl}assetsConfig.json?` + Date.now())
          .then((result) => {
            this.buildNumber = result.data.buildNumber;
            this.initStage();
            this.setBaseSize();
          });
      });
  },

  computed: {
    staticUrl() {
      return getStaticUrl(null, this.$route.name);
    },
    
  },

  methods: {
    initStage() {
      this.stage = new Konva.Stage({
        container: 'container',
        height: window.innerHeight,
        width: window.innerWidth,
      });

      this.currentWidth = 1;
      this.currentHeight = 1;

      this.layer = new Konva.Layer();

      if (!!this.customProducts.length) {
        this.initCustomCanvasData();
      } else {
        this.initCanvasData();

        this.stage.add(this.layer);
        this.resizeStageToLayer();
      }
    },

    initCanvasData() {
      this.canvasData = FamilyPreviewService.getFamilyPreview(
        this.selectedFamily
      );

      this.clusters = this.canvasData.familyClusters;
      this.products = this.canvasData.familyProducts;
      let totalWith = 0;

      this.clusters.forEach((item) => {
        totalWith += item.width;
      });

      const widthDelta = this.stage.width() - totalWith;

      //If delta is negative so scaling is applied
      const delta = Math.abs(widthDelta);
      const centerDelta = delta / 2;

      this.clusters.forEach((item) => {
        const clusterGroup = new Konva.Group({
          id: FamilyPreviewService.getClusterName(item.clusterName),
          name: 'cluster',
        });
        const clusterLineLeft = new Konva.Line({
          height: item.height * this.currentHeight,
          width: item.width * this.currentWidth,
          points: [
            item.pointX * this.currentWidth + centerDelta,
            item.pointY * this.currentHeight,
            item.pointX * this.currentWidth + centerDelta,
            item.height * this.currentHeight,
          ],
          strokeWidth: 3,
        });
        clusterLineLeft.strokeLinearGradientStartPoint({
          x: item.pointX * this.currentWidth + centerDelta,
          y: item.pointY * this.currentHeight,
        });
        clusterLineLeft.strokeLinearGradientEndPoint({
          x: item.pointX * this.currentWidth + centerDelta,
          y: item.height * this.currentHeight,
        });
        clusterLineLeft.strokeLinearGradientColorStops([
          0.5,
          '#181818',
          0.9,
          '#adadad',
        ]);

        const clusterLineBottom = new Konva.Line({
          height: item.height * this.currentHeight,
          width: item.width * this.currentWidth,
          points: [
            item.pointX * this.currentWidth + centerDelta,
            item.height * this.currentHeight,
            (item.width + item.pointX) * this.currentWidth + centerDelta,
            item.height * this.currentHeight,
          ],
          stroke: '#adadad',
          strokeWidth: 3,
        });

        const clusterLineRight = new Konva.Line({
          height: item.height * this.currentHeight,
          width: item.width * this.currentWidth,
          points: [
            (item.width + item.pointX) * this.currentWidth + centerDelta,
            item.height * this.currentHeight,
            (item.width + item.pointX) * this.currentWidth + centerDelta,
            item.pointY * this.currentHeight,
          ],
          strokeWidth: 3,
        });
        clusterLineRight.strokeLinearGradientStartPoint({
          x: item.pointX * this.currentWidth + centerDelta,
          y: item.pointY * this.currentHeight,
        });
        clusterLineRight.strokeLinearGradientEndPoint({
          x: item.pointX * this.currentWidth + centerDelta,
          y: item.height * this.currentHeight,
        });
        clusterLineRight.strokeLinearGradientColorStops([
          0.5,
          '#181818',
          0.9,
          '#adadad',
        ]);

        const clusterName = new Konva.Label({
          x: item.pointX * this.currentWidth + centerDelta,
          y: (item.height - 15) * this.currentHeight,
        });

        clusterName.add(
          new Konva.Tag({
            fill: '#181818',
          })
        );

        const clusterText = new Konva.Text({
          text: FamilyPreviewService.getClusterName(item.clusterName),
          fontSize: 22 * this.currentHeight,
          padding: 5,
          fill: CONFIG.text.fill,
          fontFamily: CUSTOM_FONT_FAMILY
        });

        clusterName.add(clusterText);

        if (clusterName.width() >= item.width * this.currentWidth) {
          clusterText.width(item.width * this.currentWidth - 6);
          clusterText.fontSize(!!localStorage.getItem('isElectron') ? 23.5 * this.currentHeight : 22 * this.currentHeight);
          clusterText.align('left');
          clusterName.x(clusterName.x() + 3);
        } else {
          clusterName.x(
            clusterName.x() +
              (item.width / 2) * this.currentWidth -
              clusterName.width() / 2
          );
        }

        clusterGroup.add(clusterLineLeft);
        clusterGroup.add(clusterLineBottom);
        clusterGroup.add(clusterLineRight);
        clusterGroup.add(clusterName);
        this.layer.add(clusterGroup);
      });
      this.layer.hide();
      this.products.forEach((product) => {
        this.initProduct(product, centerDelta);
      });
    },

    initProduct(product, delta) {
      const imageGroup = new Konva.Group({
        id: product.productId,
        name: 'product',
      });
      const image = new Image();
      image.src =
        `${this.staticUrl}Images/${product.orthoPath}` + '?' + this.buildNumber;

      const productImage = new Konva.Image({
        id: product.productId,
        image: image,
        x: product.pointX * this.currentWidth + delta,
        y: product.pointY * this.currentHeight,
        scaleX:
          product.scaleX === 0
            ? 0.1 * this.currentWidth
            : product.scaleX * this.currentWidth,
        scaleY:
          product.scaleY === 0
            ? 0.1 * this.currentHeight
            : product.scaleY * this.currentHeight,
      });

      const productName = new Konva.Label({
        x: productImage.x(),
        y: productImage.y(),
      });

      productName.add(
        new Konva.Text({
          text: this.getProductName(product.productId),
          fontSize: !!localStorage.getItem('isElectron') ? 13.5 * this.currentHeight : 14 * this.currentHeight,
          padding: 5,
          fill: CONFIG.text.fill,
          width: !!localStorage.getItem('isElectron') ? 115 * this.currentHeight : 130 * this.currentWidth,
          align: 'center',
          fontFamily: CUSTOM_FONT_FAMILY
        })
      );
      const self = this;
      image.onload = function () {
        self.totalProducts++;
        productName.y(productImage.y() + image.height * productImage.scaleY());
        productName.x(
          productImage.x() +
            (image.width * productImage.scaleX()) / 2 -
            productName.width() / 2
        );
      };

      // click on image
      productImage.on('click', () =>
        this.$emit('showSelectedProduct', productImage.id())
      );
      productImage.on('touchend', () =>
        this.$emit('showSelectedProduct', productImage.id())
      );
      productImage.on(
        'mouseenter',
        () => (this.stage.container().style.cursor = 'pointer')
      );
      productImage.on(
        'mouseleave',
        () => (this.stage.container().style.cursor = 'default')
      );

      // click on text
      productName.on('click', () =>
        this.$emit('showSelectedProduct', productImage.id())
      );
      productName.on('touchend', () =>
        this.$emit('showSelectedProduct', productImage.id())
      );
      productName.on(
        'mouseenter',
        () => (this.stage.container().style.cursor = 'pointer')
      );
      productName.on(
        'mouseleave',
        () => (this.stage.container().style.cursor = 'default')
      );

      imageGroup.add(productImage);
      imageGroup.add(productName);
      this.layer.add(imageGroup);
    },

    initCustomCanvasData() {
      this.canvasData = FamilyPreviewService.getFamilyPreview(
        this.selectedFamily
      );
      this.clusters = this.canvasData.familyClusters;
      this.products = this.canvasData.familyProducts;

      this.loadProductsWithCallback(this.customProducts, (loadedProducts) => {
        let totalWith = 0;
        let x = 0;
        this.clusters.forEach((item) => {
          if (loadedProducts.find((p) => p.clusterName === item.clusterName)) {
            var clusterHeight = item.height;
            item.loadedProducts = loadedProducts.filter(
              (p) => p.clusterName == item.clusterName
            );
            item.width = this.calculateClusterWidth(
              item.loadedProducts,
              clusterHeight
            );
            totalWith += item.width;
          }
        });

        const delta =
          this.stage.width() - totalWith < 0
            ? 20
            : this.stage.width() - totalWith;
        this.clusters.forEach((item) => {
          if (loadedProducts.find((p) => p.clusterName === item.clusterName)) {
            const clusterGroup = new Konva.Group({
              id: item.clusterName,
              name: 'cluster',
              width: item.width,
              height: item.height,
            });

            if (this.currentHeight < 1) {
              item.height = item.height - 50;
            }

            const clusterLineLeft = new Konva.Line({
              name: 'clusterLineLeft',
              height: item.height,
              width: item.width,
              points: [x + delta / 2, item.pointY, x + delta / 2, item.height],
              strokeWidth: 3,
            });
            clusterLineLeft.strokeLinearGradientStartPoint({
              x: x + delta / 2,
              y: item.pointY,
            });
            clusterLineLeft.strokeLinearGradientEndPoint({
              x: x + delta / 2,
              y: item.height,
            });
            clusterLineLeft.strokeLinearGradientColorStops([
              0.5,
              '#181818',
              0.9,
              '#adadad',
            ]);

            const clusterLineBottom = new Konva.Line({
              name: 'clusterLineBottom',
              height: item.height,
              width: item.width,
              points: [
                x + delta / 2,
                item.height,
                item.width + x + delta / 2,
                item.height,
              ],
              stroke: '#adadad',
              strokeWidth: 3,
            });

            const clusterLineRight = new Konva.Line({
              name: 'clusterLineRight',
              height: item.height,
              width: item.width,
              points: [
                item.width + x + delta / 2,
                item.height,
                item.width + x + delta / 2,
                item.pointY,
              ],
              strokeWidth: 3,
            });
            clusterLineRight.strokeLinearGradientStartPoint({
              x: x + delta / 2,
              y: item.pointY,
            });
            clusterLineRight.strokeLinearGradientEndPoint({
              x: x + delta / 2,
              y: item.height,
            });
            clusterLineRight.strokeLinearGradientColorStops([
              0.5,
              '#181818',
              0.9,
              '#adadad',
            ]);

            const clusterName = new Konva.Label({
              x: x + delta / 2,
              y: item.height - 18,
            });

            clusterName.add(
              new Konva.Tag({
                fill: '#181818',
              })
            );

            const clusterText = new Konva.Text({
              text: FamilyPreviewService.getClusterName(item.clusterName),
              fontSize: 24,
              padding: 5,
              fill: CONFIG.text.fill,
              fontFamily: CUSTOM_FONT_FAMILY
            });

            clusterName.add(clusterText);

            if (clusterName.width() >= clusterGroup.width()) {
              clusterText.width(clusterGroup.width() - 6);
              clusterText.fontSize(23.5);
              clusterText.align('left');
              clusterName.x(clusterName.x() + 3);
            } else {
              clusterName.x(
                clusterName.x() + item.width / 2 - clusterName.width() / 2
              );
            }

            clusterGroup.add(clusterLineLeft);
            clusterGroup.add(clusterLineBottom);
            clusterGroup.add(clusterLineRight);
            clusterGroup.add(clusterName);
            this.layer.add(clusterGroup);

            this.layer.hide();
            this.buildCustomImage(item, clusterGroup);
            x += item.width + 20;
          }
        });

        this.stage.add(this.layer);
        this.resizeStageToLayer(true);
      });
    },

    setBaseSize() {
      this.baseHeight = this.stage.height();
      this.baseWidth = this.stage.width();
    },

    changeSize() {
      this.stage.width(window.innerWidth);
      this.stage.height(window.innerHeight);
      
      this.resizeStageToLayer();
    },

    resizeStageToLayer(isCustomSelection = false) {
      const layerHeight = this.layer.canvas.height;
      let stageHeight = window.innerHeight;
      const stageWidth = window.innerWidth;
      if(stageHeight > layerHeight) {
        console.log('no scale at all')
        return;
      }
      
      stageHeight = 
        window.innerHeight - 
        (HEADER_BLOCK_HEIGHT * 2 + MARGIN_HEADER_BLOCK) - 
        (isCustomSelection ? CUSTOM_SELECTION_TITLE_HEIGHT + MARGIN_HEADER_BLOCK : 0);

      const paddingX = 100;
      const paddingY = 0;

      const box = this.layer.getClientRect({ relativeTo: this.stage });
      const scale = Math.min(
        stageWidth / (box.width + paddingX * 2),
        stageHeight / (box.height + paddingY)
      );

      const scaledLayerWidth = -box.x * scale + box.width * scale;
      const delta = stageWidth - scaledLayerWidth > 0 
        ? (stageWidth - scaledLayerWidth) / 2
        : 0;

      this.stage.setAttrs({
        x: -box.x * scale + delta + paddingX * scale,
        y: -box.y * scale + paddingY * scale,
        scaleX: scale,
        scaleY: scale
      });
    },

    resizeShapes() {
      const scaleX = this.stage.width() / this.baseWidth;
      const scaleY = this.stage.height() / this.baseHeight;

      this.layer.scaleX(scaleX);
      this.layer.scaleY(scaleY);

      this.layer.children.forEach((shape) => {
        shape.scaleX(this.layer.scaleY());
        shape.scaleY(this.layer.scaleX());
      });

      this.layer.draw();
    },

    attachProductEvents(productImage, productName) {
      // click on image
      productImage.on('click', () =>
        this.$emit('showSelectedProduct', productImage.id())
      );
      productImage.on(
        'mouseenter',
        () => (this.stage.container().style.cursor = 'pointer')
      );
      productImage.on(
        'mouseleave',
        () => (this.stage.container().style.cursor = 'default')
      );

      // click on text
      productName.on('click', () =>
        this.$emit('showSelectedProduct', productImage.id())
      );
      productName.on(
        'mouseenter',
        () => (this.stage.container().style.cursor = 'pointer')
      );
      productName.on(
        'mouseleave',
        () => (this.stage.container().style.cursor = 'default')
      );
    },

    createGroupImage(imageGroup, productImage, productName) {
      imageGroup.add(productImage);
      imageGroup.add(productName);
      this.layer.add(imageGroup);
    },

    buildCustomImage(cluster, clusterGroup) {
      const clusterNode = this.layer.getChildren(
        (node) => node.attrs.id === cluster.clusterName
      )[0];
      const leftLine = clusterNode.children[0];
      const bottomLine = clusterNode.children[1];

      const startX = leftLine.attrs.points[0] + 2 * MARGIN_RIGHT;
      const stageHeight = bottomLine.attrs.points[1];
      this.drawProducts(
        cluster.loadedProducts,
        clusterGroup,
        startX,
        stageHeight
      );
    },

    getProductName(productId) {
      const name = this.$store.state.radios.find(
        (item) => item.productId === productId
      )?.name;

      return NAMES.hasOwnProperty(name) ? NAMES[name] : name;
    },

    drawProducts(products, clusterGroup, startX, stageHeight) {
      const sortedProducts = this.getSortedProducts(products);
      let productMaxWidth = 0;

      var initialX = startX;
      var columnHeight = 0;
      var startY = stageHeight - (sortedProducts[0].height + NAME_BLOCK_HEIGHT);

      for (let i = 0; i < sortedProducts.length; i++) {
        const product = sortedProducts[i];
        const productWithNameHeight = product.height + NAME_BLOCK_HEIGHT;
        columnHeight += productWithNameHeight;

        this.drawProductGroup(clusterGroup, product, startX, Math.abs(startY));
        if (i + 1 < sortedProducts.length) {
          if (product.width > productMaxWidth) {
            productMaxWidth = product.width;
          }
          const nextProduct = sortedProducts[i + 1];
          const nextProductWithNameHeight =
            nextProduct.height + NAME_BLOCK_HEIGHT;
          const nextProductWithMarginWidth = initialX + productMaxWidth;
          const isProductShifted =
            stageHeight - columnHeight < nextProductWithNameHeight;

          initialX = isProductShifted ? nextProductWithMarginWidth : initialX;

          startY = isProductShifted
            ? stageHeight - nextProductWithNameHeight
            : startY - nextProductWithNameHeight;

          const nextProductMargin =
            (product.width - nextProduct.width) / 2 + (startX - initialX);

          startX = isProductShifted
            ? nextProductWithMarginWidth
            : initialX + nextProductMargin;
          columnHeight = isProductShifted ? 0 : columnHeight;
          productMaxWidth = isProductShifted ? 0 : productMaxWidth + 3;
        }
      }
    },

    drawProductGroup(clusterGroup, product, startX, startY) {
      const productId = product.product.productId;
      const imageGroup = new Konva.Group({
        name: 'product',
        id: productId,
      });

      const productImage = new Konva.Image({
        id: productId,
        image: product.image,
        x: startX,
        y: startY,
        scaleX: product.scaleX,
        scaleY: product.scaleY,
        height: product.image.height,
        width: product.image.width,
      });

      const productText = new Konva.Text({
        text: this.getProductName(product.product.productId),
        fontSize: 14,
        padding: 4,
        fill: CONFIG.text.fill,
        width: 110,
        align: 'center',
        fontFamily: CUSTOM_FONT_FAMILY
      });

      const productName = new Konva.Label({
        x:
          productImage.x() +
          (productImage.width() * productImage.scaleX()) / 2 -
          productText.width() / 2,
        y: productImage.y() + productImage.height() * productImage.scaleY(),
      });

      productName.add(productText);
      this.attachProductEvents(productImage, productName);

      imageGroup.add(productImage);
      imageGroup.add(productName);
      clusterGroup.add(imageGroup);
      this.totalProducts++;
    },
    loadProductsWithCallback(customProducts, callback) {
      const selectedProducts = this.canvasData.familyProducts.filter((p) =>
        customProducts.includes(p.productId)
      );
      var loadedSelectedProducts = [];

      selectedProducts.forEach((product) => {
        const productData = this.$store.state.radios.find(
          (p) => p.productId === product.productId
        );
        const image = new Image();
        image.src =
          `${this.staticUrl}Images/${product.orthoPath}` +
          '?' +
          this.buildNumber;
        image.onload = () => {
          const scaleX = product.scaleX === 0 ? 0.1 : product.scaleX;
          const scaleY = product.scaleY === 0 ? 0.1 : product.scaleY;
          loadedSelectedProducts.push({
            product: product,
            clusterName: productData.cluster,
            image: image,
            scaleX: scaleX,
            scaleY: scaleY,
            x: product.pointX,
            y: product.pointY,
            imageHeight: image.height,
            imageWidth: image.width,
            height: image.height * scaleY,
            width: image.width * scaleX,
          });

          if (selectedProducts.length === loadedSelectedProducts.length) {
            callback(loadedSelectedProducts);
          }
        };
      });
    },
    getSortedProducts(products) {
      return products.sort((a, b) => {
        if (
          (a.x < b.x &&
            (a.y > b.y || a.height > b.height || a.width > b.width)) ||
          (a.x + a.width < b.x && a.y > b.y)
        ) {
          return -1;
        } else {
          return 1;
        }
      });
    },
    calculateClusterWidth(products, clusterHeight) {
      clusterHeight =
        this.currentWidth === 1 ? clusterHeight : clusterHeight - 50;
      const sortedProducts = this.getSortedProducts(products);
      var clusterPaddings = 2 * MARGIN_RIGHT;
      var totalWidth = sortedProducts[0].width + clusterPaddings;
      var maxProductWidth = 0;
      var columnHeight = 0;

      sortedProducts.forEach((p) => {
        const productWithNameHeight = p.height + NAME_BLOCK_HEIGHT;
        const isProductShifted =
          clusterHeight - columnHeight < productWithNameHeight;
        maxProductWidth = p.width > maxProductWidth ? p.width : maxProductWidth;
        totalWidth += isProductShifted ? maxProductWidth : 0;
        columnHeight = isProductShifted
          ? productWithNameHeight
          : columnHeight + productWithNameHeight;
        maxProductWidth = isProductShifted ? 0 : maxProductWidth;
      });

      return totalWidth + 2 * MARGIN_RIGHT;
    },
  },
};
</script>

<style lang="less" scoped>
.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>